import { AUTH_EVENTS, HttpErrorMessage } from '@aston/foundation';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthOptions } from 'angular-auth-oidc-client';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { PostLoginRouteService } from '../../shared-module/services/post-login-route.service';
import * as AppStoreActions from '../../root-store/app-store/actions';
import { IResponseToken } from '../models';

import { AuthenticationStorageService } from './authentication.storage.service';

const debug = (...args) => window['__aston_debug_auth'] && console.debug(...args)

@Injectable({
	providedIn: 'root'
})
export class AuthenticationService {

	private postLoginRouteService = inject(PostLoginRouteService)
	private oidcSecurityService = inject(OidcSecurityService)
	private storage = inject(AuthenticationStorageService)
	private store = inject(Store)

	getIsAuthenticated(): Observable<boolean> {
		return this.oidcSecurityService.isAuthenticated$.pipe(map(auth => auth.isAuthenticated));
	}

	getIsAuthorized(): Observable<boolean> {
		return this.oidcSecurityService.checkAuth().pipe(
			map(response => {
				const auth = response.isAuthenticated;

				if (auth) {
					debug('[auth] subscribed for cross logout')
					this.storage.onKeyChange(AUTH_EVENTS.Logout, _ => setTimeout(_ => this.propagatedLogout(), 100))

					this.store.dispatch(AppStoreActions.Login());
					this.store.dispatch(AppStoreActions.LoadUserRequest());

					const postLoginRoute = this.postLoginRouteService.getPostLoginRoute();
					if (postLoginRoute) {
						setTimeout(_ => {
							this.store.dispatch(AppStoreActions.Navigate({to: [postLoginRoute]}));
							this.postLoginRouteService.removePostLoginRoute();
						}, 100);
					}
				}
				return auth;
			}),
			catchError((error: HttpErrorMessage) => {
				if (error.code === 401) {
					// auth was ok but /me returned a 401
					// that must be bc the user is not known by the app API
					// so we just logout the user
					debug('[auth] this user is not registered on the app.');
					this.logout();
				}
				return throwError(error);
			})
		);
	}

	login(authOptions?: AuthOptions): void {
		this.oidcSecurityService.authorize(null, authOptions);
	}

	propagatedLogout(): void {
		debug('[auth] propagated logout')
		this.storage.remove(AUTH_EVENTS.Logout)
		window.location.assign(window.location.origin)
	}

	logout(): void {
		debug('[auth] logout')
		this.oidcSecurityService.logoffAndRevokeTokens().subscribe(_ => {
			debug('[auth] dispatch logout propagation')
			this.storage.dispatch(AUTH_EVENTS.Logout)
		})
		this.store.dispatch(AppStoreActions.Logout());
	}

	revokeTokens() {
		this.oidcSecurityService.logoffLocal();
		this.oidcSecurityService.revokeAccessToken();
		this.oidcSecurityService.revokeRefreshToken();
	}

	refreshToken(): Observable<Partial<IResponseToken>> {
		return this.oidcSecurityService.forceRefreshSession().pipe(
			map(token => ({
				access_token: token.accessToken,
				id_token: token.idToken,
			}))
		);
	}

	getAccessToken(): Observable<string> {
		return this.oidcSecurityService.getAccessToken()
	}

	getAccessTokenExpiration(): Observable<number> {
		// see https://github.com/damienbod/angular-auth-oidc-client/blob/55680e56ed5898407733fb3f4512f91c8a0d4b09/projects/angular-auth-oidc-client/src/lib/auth-state/auth-state.service.ts#L176
		return this.oidcSecurityService.getConfiguration().pipe(
			map(config => (this.storage.read(config.configId) as any)),
			map(store => JSON.parse(store)),
			map(store => store?.access_token_expires_at),
			catchError(_ => of(null)),
		)
	}

	renewTimeBeforeTokenExpires() {
		return this.oidcSecurityService.getConfiguration().pipe(
			map(config => config.renewTimeBeforeTokenExpiresInSeconds)
		)
	}

	isLoggedIn(): Observable<boolean> {
		return this.oidcSecurityService.isAuthenticated();
	}

	getTenantId(): string {
		return null // mostly for compatibility
	}
}
